import { useCallback, useEffect, useRef, useState } from "react";
import { extractPlaybackStreamInfo } from "../lib/createPlaybackStreamCommand";
import { PlaybackClipRecorder } from "../lib/clipRecorder";

const { MultipartParser, parseMultipartBoundary } = require("../lib/multipart");

const RECONNECT_MAX_ATTEMPTS = 3;
const RECONNECT_MIN_PROGRESS_MS = 2000;
const RECONNECT_END_MARGIN_SECONDS = 3;
const STREAM_IDLE_TIMEOUT_MS = 20000;

export const CLIP_DOWNLOAD_CANCELLED = "clip-download-cancelled";

async function readSession({ streamUrl, signal, recorder, onData }) {
  const controller = new AbortController();
  const abort = () => controller.abort();
  signal.addEventListener("abort", abort);
  let idleTimer = null;
  let idledOut = false;
  const resetIdle = () => {
    clearTimeout(idleTimer);
    idleTimer = setTimeout(() => {
      idledOut = true;
      controller.abort();
    }, STREAM_IDLE_TIMEOUT_MS);
  };
  try {
    resetIdle();
    const response = await fetch(streamUrl, { signal: controller.signal });
    if (!response.ok || !response.body) throw new Error("Playback stream unavailable.");
    const parser = new MultipartParser({
      boundary: parseMultipartBoundary(response.headers.get("content-type") || ""),
      onPart: (part) => recorder.push(part),
    });
    const reader = response.body.getReader();
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      resetIdle();
      parser.push(value);
      onData();
    }
    try {
      parser.finish();
    } catch (_error) {
      // A truncated tail only loses the last partial frame.
    }
  } catch (error) {
    if (signal.aborted) throw error;
    if (!idledOut && error?.name !== "AbortError" && !recorder.hasVideo) throw error;
  } finally {
    clearTimeout(idleTimer);
    signal.removeEventListener("abort", abort);
  }
}

/**
 * Downloads [startTime, endTime] as an MP4 by replaying the recording through
 * its own createPlaybackStream session (independent of the on-screen player).
 */
export function useClipDownload({ requestCreatePlaybackStream, cancelPending }) {
  const [exporting, setExporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const abortRef = useRef(null);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    cancelPending?.();
  }, [cancelPending]);

  useEffect(() => () => {
    abortRef.current?.abort();
    cancelPending?.();
  }, [cancelPending]);

  const download = useCallback(async ({ deviceId, siteId, streamId, startTime, endTime }) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const { signal } = controller;
    const totalMs = (endTime - startTime) * 1000;
    const recorder = new PlaybackClipRecorder({ maxDurationMs: totalMs });

    setExporting(true);
    setProgress(0);
    try {
      let resumeSec = startTime;
      let lastResumeMs = null;
      let attempts = 0;
      for (;;) {
        const response = await requestCreatePlaybackStream({
          deviceId,
          siteId,
          streamId,
          startTime: resumeSec,
          endTime,
        });
        if (signal.aborted) break;
        const streamInfo = extractPlaybackStreamInfo(response);
        if (!streamInfo) throw new Error("Playback stream is unavailable.");

        recorder.expectResume();
        await readSession({
          streamUrl: streamInfo.streamUrl,
          signal,
          recorder,
          onData: () => setProgress(Math.min(0.99, recorder.recordedMs / totalMs)),
        });
        if (signal.aborted) break;

        const recordedMs = recorder.recordedMs;
        const nextResumeSec = startTime + Math.max(0, Math.floor(recordedMs / 1000) - 1);
        if (!recorder.hasVideo || nextResumeSec >= endTime - RECONNECT_END_MARGIN_SECONDS) break;
        const progressed = lastResumeMs === null || recordedMs - lastResumeMs >= RECONNECT_MIN_PROGRESS_MS;
        attempts = progressed ? 1 : attempts + 1;
        if (attempts > RECONNECT_MAX_ATTEMPTS) break;
        lastResumeMs = recordedMs;
        resumeSec = nextResumeSec;
      }
      if (signal.aborted) throw new Error(CLIP_DOWNLOAD_CANCELLED);

      const result = await recorder.finalize();
      setProgress(1);
      return {
        ...result,
        blob: new Blob([result.buffer], { type: "video/mp4" }),
      };
    } catch (error) {
      if (signal.aborted || error?.message === "createPlaybackStream superseded") {
        throw new Error(CLIP_DOWNLOAD_CANCELLED);
      }
      throw error;
    } finally {
      if (abortRef.current === controller) {
        abortRef.current = null;
        setExporting(false);
      }
    }
  }, [requestCreatePlaybackStream]);

  return { download, cancel, exporting, progress };
}
